import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { Column } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import IconChevronDown from '~icons/ci/chevron-down';
import IconChevronUp from '~icons/ci/chevron-up';

import { LineClamp } from '@adflow/frontend/ui-common';
import { su, theme } from '@adflow/frontend/ui-theme';

interface HeaderCellProps<T> {
	text?: string | ReactNode;
	align?: string;
	column: Column<T>;
	thinStyle?: boolean;
	textStyle?: CSSInterpolation;
	beforeText?: ReactNode;
}

export const headerCellStyles = {
	container: css({
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		height: '100%',
		padding: '0.5rem 0',
	}),
	text: css(su.textXs, {
		color: theme.colors.text.tableHeader,
		fontWeight: '400',
	}),
	icon: css({
		color: theme.colors.text.tableHeader,
		marginRight: '-0.25rem',
	}),
	rightAlign: css({ justifyContent: 'flex-end' }),
	centerAlign: css({ justifyContent: 'center' }),
	disabled: css({
		color: theme.colors.text.disabled,
	}),
};

export function HeaderCell<T>({
	column,
	align,
	text = column.columnDef.meta?.name,
	thinStyle,
	textStyle,
	beforeText,
}: HeaderCellProps<T>) {
	const sortDirection = column.getIsSorted();
	const IconSort = useMemo(() => {
		if (sortDirection === 'desc') return IconChevronDown;
		if (sortDirection === 'asc') return IconChevronUp;
		return null;
	}, [sortDirection]);
	return (
		<div
			css={[
				headerCellStyles.container,
				align === 'right'
					? headerCellStyles.rightAlign
					: align === 'center'
						? headerCellStyles.centerAlign
						: null,
				thinStyle && { paddingLeft: '0.25rem', paddingRight: '0.25rem' },
			]}
		>
			{beforeText}
			<LineClamp
				lines={2}
				css={[
					headerCellStyles.text,
					!column.getCanSort() && headerCellStyles.disabled,
					textStyle,
				]}
			>
				{text}
			</LineClamp>
			{IconSort ? (
				<div>
					<IconSort
						css={[
							headerCellStyles.icon,
							!column.getCanSort() && headerCellStyles.disabled,
						]}
						width={20}
						height={20}
					/>
				</div>
			) : null}
		</div>
	);
}
